import type { Dayjs } from 'dayjs';
import { AcceptedCurrency, Trip, TripTag, TripTagsKey } from '../../types/trip';

export const currencySymbols: Record<AcceptedCurrency, string> = {
  gbp: '£',
  eur: '€',
  usd: '$',
  cad: '$',
  aud: '$',
  nzd: '$',
  hkd: '$',
  aed: 'DH',
  chf: 'CHF',
};

export const getTripsTypes = () => {
  const tripTypesArray = Object.keys(TripTag).map((key) => ({
    label: TripTag[key as keyof typeof TripTag],
    value: key,
  }));
  return tripTypesArray.map((type) => {
    return {
      label: type.label as string,
      value: type.value as string,
      category: 'trip types',
    };
  });
};

const filterTripsByContinent = (trips: Trip[], continents: string[]) => {
  return trips.filter((trip) => {
    return continents.some((continent) => trip.continents.includes(continent));
  });
};

const filterTripsByActivityLevel = (trips: Trip[], activityLevels: string[]) => {
  return trips.filter((trip) => {
    return activityLevels.some((activityLevel) => {
      return trip.activityLevels.includes(activityLevel);
    });
  });
};

export const filterTripsByCountry = (trips: Trip[], countries: string[]) => {
  return trips.filter((trip) => {
    return countries.some((country) => trip.countries.includes(country));
  });
};

const filterTripsByTripType = (trips: Trip[], tripTypes: string[]) => {
  return trips.filter((trip) => {
    return tripTypes.some((type) => trip.types && trip.types.includes(TripTagsKey(type)));
  });
};

const filterByPriceFrom = (trips: Trip[], priceFrom: number) => {
  return trips.filter((trip) => {
    return trip.priceRange.min.amount >= priceFrom;
  });
};

const filterByPriceTo = (trips: Trip[], priceTo: number) => {
  return trips.filter((trip) => {
    return trip.priceRange.min.amount <= priceTo;
  });
};

const filterByDate = (trips: Trip[], fromDate: Dayjs | null, toDate: Dayjs | null) => {
  const fromDateDate = fromDate?.toDate().toISOString();
  const toDateDate = toDate?.toDate().toISOString();

  if (!fromDateDate && !toDateDate) {
    return trips;
  }

  return trips.filter((trip) => {
    return trip.availableDates.some((departureDate) => {
      const departureDateString = departureDate;
      if (fromDateDate && departureDateString < fromDateDate) return false;
      if (toDateDate && departureDateString > toDateDate) return false;
      return true;
    });
  });
};

export const filterByMinDuration = (trips: Trip[], minDuration: number) =>
  trips.filter((trip) => trip.duration >= minDuration);

export const filterByMaxDuration = (trips: Trip[], maxDuration: number) =>
  trips.filter((trip) => trip.duration <= maxDuration);

export const filterTrips = (
  trips: Trip[],
  filters: AllTripsSearchState,
  sortCheapestFirst: boolean = false,
) => {
  let filteredTrips = trips;
  if (filters.continents.length > 0) {
    filteredTrips = filterTripsByContinent(filteredTrips, filters.continents);
  }
  if (filters.activityLevel.length > 0) {
    filteredTrips = filterTripsByActivityLevel(filteredTrips, filters.activityLevel);
  }
  if (filters.countries.length > 0) {
    filteredTrips = filterTripsByCountry(filteredTrips, filters.countries);
  }
  if (filters.tripTypes.length > 0) {
    filteredTrips = filterTripsByTripType(filteredTrips, filters.tripTypes);
  }
  if (filters.priceFrom) {
    filteredTrips = filterByPriceFrom(filteredTrips, filters.priceFrom);
  }
  if (filters.priceTo) {
    filteredTrips = filterByPriceTo(filteredTrips, filters.priceTo);
  }
  if (filters.fromDate || filters.toDate) {
    filteredTrips = filterByDate(filteredTrips, filters.fromDate, filters.toDate);
  }
  if (filters.minDuration) {
    filteredTrips = filterByMinDuration(filteredTrips, filters.minDuration);
  }
  if (filters.maxDuration) {
    filteredTrips = filterByMaxDuration(filteredTrips, filters.maxDuration);
  }
  if (filters.tripTypes.length > 0) {
    const countTripTypeMatches = (trip: Trip) => {
      const filtersValuesArray = filters.tripTypes.map((type) => TripTagsKey(type));
      return trip.types.filter((type) => filtersValuesArray.includes(type)).length;
    };

    filteredTrips.sort((a, b) => {
      const aMatches = countTripTypeMatches(a);
      const bMatches = countTripTypeMatches(b);
      if (aMatches === bMatches) {
        return a.priceRange.min.amount - b.priceRange.min.amount;
      } else {
        return bMatches - aMatches;
      }
    });
  } else if (sortCheapestFirst) {
    filteredTrips = filteredTrips.sort((a, b) => {
      return a.priceRange.min.amount - b.priceRange.min.amount;
    });
  }

  return filteredTrips;
};

export type AllTripsSearchState = {
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  countries: string[];
  continents: string[];
  activityLevel: string[];
  priceFrom: number | null;
  priceTo: number | null;
  tripTypes: string[];
  minDuration: number | null;
  maxDuration: number | null;
};

export const searchStateToDisplayString = (
  {
    fromDate,
    toDate,
    countries,
    continents,
    activityLevel,
    tripTypes,
    priceFrom,
    priceTo,
  }: AllTripsSearchState,
  currency: AcceptedCurrency,
): string => {
  const formatDate = (date: Dayjs) => date.format('YYYY-MM');
  const formatPrice = (price: number) =>
    new Intl.NumberFormat(navigator.language, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  const formatStrings = (strings: string[]) =>
    strings
      .map((s) =>
        s
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' '),
      )
      .join(', ');
  const addPrefix = (prefix: string, value: string) => `${prefix} ${value}`;

  return [
    fromDate && addPrefix('From', formatDate(fromDate)),
    toDate && addPrefix('To', formatDate(toDate)),
    formatStrings(countries),
    formatStrings(continents),
    formatStrings(activityLevel),
    formatStrings(tripTypes),
    priceFrom && addPrefix('Min', formatPrice(priceFrom)),
    priceTo && addPrefix('Max', formatPrice(priceTo)),
  ]
    .filter(Boolean)
    .join(', ');
};

export type AllTripsSearchParams = Partial<Record<keyof AllTripsSearchState, string>>;

export const defaultSearchState: AllTripsSearchState = {
  fromDate: null,
  toDate: null,
  countries: [],
  continents: [],
  activityLevel: [],
  priceFrom: null,
  priceTo: null,
  tripTypes: [],
  minDuration: null,
  maxDuration: null,
};
